import React, { useState } from 'react';
import {
  TableRow,
  TableCell,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLanguageNavigation } from '../hooks/useLanguageNavigation';
import { TRANSACTIONS_ENABLED } from '../config/featureFlags';

const WalletRow = ({ wallet, onDelete, visibleColumns, columnRenderers, allColumns, hasError, errorMessage, handleRefreshWallet, currentPrices, formatCurrency, isRTL }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const { navigateWithLang } = useLanguageNavigation();

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = (event) => {
    event.stopPropagation();
    handleMenuClose();
    onDelete(wallet.id);
  };

  const handleRefresh = (event) => {
    event.stopPropagation();
    handleMenuClose();
    handleRefreshWallet(wallet.id);
  };

  const handleTransactions = (event) => {
    event.stopPropagation();
    handleMenuClose();
            navigateWithLang(`wallet-transactions?walletId=${wallet.id}`);
  };

  return (
    <TableRow 
      sx={{ 
        '& > *': { borderBottom: 'unset' },
        ...(hasError && {
          backgroundColor: '#ffebee',
          '&:hover': {
            backgroundColor: '#ffcdd2'
          }
        })
      }}
    >
      <TableCell align="center" sx={{ p: 0, width: 0, minWidth: 0, maxWidth: 36, textAlign: 'center' }}>
        <IconButton
          aria-label="more"
          aria-controls={anchorEl ? 'wallet-menu' : undefined}
          aria-haspopup="true"
          onClick={handleMenuClick}
          size="small"
          sx={{ m: 0, p: 0 }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          id="wallet-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleRefresh}>
            <ListItemIcon>
              <RefreshIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('common.refresh')}</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDelete}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('common.delete')}</ListItemText>
          </MenuItem>
          {TRANSACTIONS_ENABLED && (
            <MenuItem onClick={handleTransactions}>
              <ListItemIcon>
                <ListAltIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('common.transactions')}</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </TableCell>
      {(visibleColumns || []).map((colId) => {
        const column = allColumns.find(c => c.id === colId);
        const isLabelCol = colId === 'label';
        return (
          <TableCell
            key={colId}
            align={isLabelCol && isRTL ? 'right' : (column?.align || 'left')}
            sx={{
              ...(isLabelCol && isRTL ? { textAlign: 'right' } : {}),
              ...(isLabelCol ? { overflow: 'hidden', textOverflow: 'ellipsis', ...(isRTL ? { paddingRight: 0 } : {}) } : {}),
              ...(hasError && {
                color: '#d32f2f',
                fontWeight: 'bold'
              })
            }}
          >
            {isLabelCol ? (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'bold',
                  textAlign: isRTL ? 'right' : 'left',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {wallet.label || 'N/A'}
              </Typography>
            ) : (
              columnRenderers[colId]?.(wallet, hasError, errorMessage) || '-'
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

export default WalletRow; 